// import { getMock } from "../mock/mock-adaptor";
import {
  attachServiceabilityRoleHeader,
  getWebApiBaseUrl,
  isLocalMachine,
  rewriteGatewayRequestForLocalProxy,
} from "../utils/api/controlProxy";
import { config } from "../utils/config";
import { HttpClient } from "./httpClient";
/**
 * Represents an API function class extending HttpClient for making HTTP requests.
 * @extends HttpClient
 */
export class InterceptorClient extends HttpClient {
  /**
   * Map of class instances keyed by baseUrl (hostname).
   * Each hostname gets its own HttpClient instance with separate connection pools.
   * @type {Map<string, InterceptorClient>}
   */
  // eslint-disable-next-line no-undef
  static instanceMap = new Map();

  isServer = typeof window === "undefined";

  /**
   * Create a new instance of InterceptorClient.
   * @param {string} baseUrl - The base URL for the HTTP requests.
   */
  constructor(baseUrl) {
    super(baseUrl);
    this.instance.interceptors.request.use(attachServiceabilityRoleHeader);
    this.instance.interceptors.request.use(rewriteGatewayRequestForLocalProxy);
  }

  /**
   * On local dev, browser calls to uat-new.fnp.com/control/* are same-origin proxied
   * via /control/* so auth cookies are not blocked as cross-site.
   * Gateway POST /serviceability/* is proxied per-request via /apigw/serviceability/*.
   *
   * @param {string} baseUrl
   * @returns {string}
   */
  static resolveBaseUrl = (baseUrl) => {
    if (!isLocalMachine()) {
      return baseUrl;
    }

    const controlBackendUrl = process.env.NEXT_PUBLIC_BASE_URL;

    if (baseUrl && controlBackendUrl && baseUrl === controlBackendUrl) {
      return getWebApiBaseUrl();
    }

    return baseUrl;
  };

  /**
   * Get or create an InterceptorClient instance for the given baseUrl.
   * Each hostname gets its own instance with separate connection pools.
   * @param {string} baseUrl - The base URL for the HTTP requests.
   * @returns {InterceptorClient} - The InterceptorClient instance for this baseUrl.
   */
  static initAxiosClient = (baseUrl) => {
    const resolvedBaseUrl = InterceptorClient.resolveBaseUrl(baseUrl);

    if (!InterceptorClient.instanceMap.has(resolvedBaseUrl)) {
      InterceptorClient.instanceMap.set(resolvedBaseUrl, new InterceptorClient(resolvedBaseUrl));
    }
    return InterceptorClient.instanceMap.get(resolvedBaseUrl);
  };

  static getFTSInstance = (baseUrl) => {
    const url = baseUrl ? baseUrl : process.env.NEXT_PUBLIC_BASE_URL_API;
    return InterceptorClient.initAxiosClient(url);
  };

  static getFNTSInstance = (baseUrl) => {
    const url = baseUrl ? baseUrl : process.env.NEXT_PUBLIC_BASE_URL_API;
    return InterceptorClient.initAxiosClient(url);
  };

  static getGatewayInstance = (baseUrl) => {
    const url = baseUrl ? baseUrl : process.env.NEXT_PUBLIC_R2_CLIENT_API_GATEWAY;
    return InterceptorClient.initAxiosClient(url);
  };

  static getFeedbackInstance = (baseUrl) => {
    const url = baseUrl ? baseUrl : process.env.NEXT_PUBLIC_FEEDBACK_API;
    return InterceptorClient.initAxiosClient(url);
  };

  static getServerCMSInstance = (baseUrl) => {
    const url = baseUrl ? baseUrl : process.env.NEXT_PUBLIC_CMS_SERVER_API_GATEWAY;
    return InterceptorClient.initAxiosClient(url);
  };

  /**
   * Resolves axios request timeout from override, instance default, or env.
   * @param {number} [timeout] - Optional per-request timeout override (ms).
   * @returns {number}
   */
  resolveRequestTimeout = (timeout) =>
    timeout || this.requestTimeout || parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || "5000", 10);

  /**
   * Make a POST request.
   * @param {Object} data - The data to be sent with the request.
   * @param {string} uri - The URI of the endpoint.
   * @param {boolean} [isMockApi]
   * @param {Object} [headers]
   * @param {number} [timeout] - Optional timeout override (in milliseconds).
   * @returns {Promise} - A Promise representing the result of the request.
   */
  post = async (data, uri, isMockApi, headers, timeout) => {
    const isMock = config.isMockServerOn || isMockApi;
    if (isMock) return {};

    return this.instance.post(uri, data, {
      headers,
      timeout: this.resolveRequestTimeout(timeout),
    });
  };

  /**
   * Make a GET request.
   * @param {string} uri - The URI of the endpoint.
   * @param {boolean} isMockApi - Whether to use mock API.
   * @param {Object} params - Query parameters.
   * @param {Object} headers - Request headers.
   * @param {number} timeout - Optional timeout override (in milliseconds).
   * @param {boolean} returnHeaders - When true, includes axios response headers on the returned payload.
   * @returns {Promise} - A Promise representing the result of the request.
   */
  get = async (uri, isMockApi, params, headers, timeout, returnHeaders) => {
    const isMock = config.isMockServerOn || isMockApi;
    if (isMock) return {};

    return this.instance.get(uri, {
      params,
      headers,
      timeout: this.resolveRequestTimeout(timeout),
      returnHeaders: Boolean(returnHeaders),
    });
  };

  /**
   * Make a PATCH request.
   * @param {Object} data - The data to be sent with the request.
   * @param {string} uri - The URI of the endpoint.
   * @param {number} [timeout] - Optional timeout override (in milliseconds).
   * @returns {Promise} - A Promise representing the result of the request.
   */
  update = async (data, uri, timeout) => {
    return this.instance.patch(uri, data, {
      timeout: this.resolveRequestTimeout(timeout),
    });
  };

  /**
   * Make a PUT request.
   * @param {Object} data - The data to be sent with the request.
   * @param {string} uri - The URI of the endpoint.
   * @param {boolean} [isMockApi]
   * @param {Object} [headers]
   * @param {number} [timeout] - Optional timeout override (in milliseconds).
   * @returns {Promise} - A Promise representing the result of the request.
   */
  put = async (data, uri, isMockApi, headers, timeout) => {
    const isMock = config.isMockServerOn || isMockApi;
    if (isMock) return {};

    return this.instance.put(uri, data, {
      headers,
      timeout: this.resolveRequestTimeout(timeout),
    });
  };

  /**
   * Make a DELETE request.
   * @param {string} uri - The URI of the endpoint.
   * @param {Object} [headers] - Optional axios request headers (e.g. gateway auth).
   * @param {number} [timeout] - Optional timeout override (in milliseconds).
   * @returns {Promise} - A Promise representing the result of the request.
   */
  delete = async (uri, headers, timeout) => {
    return this.instance.delete(uri, {
      ...(headers ? { headers } : {}),
      timeout: this.resolveRequestTimeout(timeout),
    });
  };
}
